// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 현장 만들기  `/new`
//
//  목업(SCREEN-DESIGN.html §01)을 그대로 따른다: 단계로 나누지 않고 한 화면,
//  오른쪽에 「만들면 이렇게 됩니다」가 실시간으로 붙는다.
//
//  이 화면이 지키는 것 — 목업의 넷에 **두 개를 더했다.**
//    ① 단계로 안 나눈다 · ② 주소를 자동 생성하지 않는다 · ③ 틀린 걸 즉시 말한다
//    ④ 기능은 프리셋을 앞에 둔다
//    ⑤ **되돌릴 수 없는 결정에만 확인창을 붙인다** — 주소가 이 제품에서 유일하다(아래 §확인)
//    ⑥ **눌러서 실패하는 유일한 경우(409)를 성공으로 되돌린다** — 아래 §제출
//
//  ⚠️ 폰에서도 된다. 막는 쪽이 오히려 코드가 는다(목업이 이미 접히는 격자다).
//     다만 최적화 대상은 아니다 — 현장 개설은 예정된 사건이지 급한 일이 아니다.
//     폰에서는 요약이 **화면 아래 고정 바**로 내려간다. 격자 접힘과 **같은 미디어쿼리**로
//     켠다(아래 <style>). 폭 판정을 JS 로 또 하면 값이 갈려서, 750px 태블릿에서
//     "한 칸으로 길게 늘어난 폼 맨 아래에 만들기 버튼이 파묻힌" 화면이 나온다.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { ApiError, humanMessage, type Project } from '../../api/client';
import { useSession } from '../../auth/SessionProvider';
import { Contact } from '../../components/Contact';
// ★ raw <Alert> 를 쓰지 마라 — theme.ts 의 모르는-이름 폴백 때문에 `color="red"` 가
//   **초록 안내 상자**로 나갔다. Notice 는 tone 을 타입으로 강제한다.
import { Notice } from '../../components/Notice';
import { Shell } from '../../components/Shell';
import { navigate } from '../../router';
import {
  AdminError,
  createProject,
  getCatalog,
  getOrgs,
  listProjects,
  SLOW_HINT_MS,
  type Catalog,
  type CreateResult,
  type InviteResultRow,
  type Org,
} from './api';
import { checkSlug } from './slug';

// ═══ 프리셋 ═══════════════════════════════════════════════════════════════
//
//  ★ **네 프리셋 모두 `share` 를 끈다.** SCREEN-DESIGN 과 DB-DESIGN §3-3 이 서로
//    다르게 적어 뒀는데(전자는 「혼합」에 share 포함, 후자는 세 프리셋에서 share 누락),
//    **둘 다 틀렸다.** `share.default_enabled = false`(002:46)이고 **공유 링크 화면이
//    v1 에 없다.** 없는 화면을 켜 놓으면 `perms` 에 `share.create` 가 들어가면서
//    누를 곳이 없는 상태가 된다 — 권한 배열과 실행 가능성이 갈리는 바로 그 버그다.

const PRESETS: { name: string; off: string[]; note: string }[] = [
  { name: 'BIM 설계검토', off: ['reality', 'phasing', 'share'], note: 'IFC 도면 검토가 중심' },
  {
    name: '드론 실사',
    off: ['props', 'tree', 'section', 'marquee', 'doc', 'phasing', 'share'],
    note: '메시만 본다',
  },
  { name: '발주처 열람', off: ['upload', 'trash', 'doc', 'snapshot', 'share'], note: '보기만' },
  { name: '혼합 (당진)', off: ['share'], note: '지금 당진 현장과 같은 구성' },
];
const DEFAULT_PRESET = '혼합 (당진)';

// ═══ 화면 ═════════════════════════════════════════════════════════════════

type Loaded = { catalog: Catalog; orgs: Org[]; projects: Project[] };

export function NewProject() {
  const { state } = useSession();
  const [data, setData] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [catalog, orgs, projects] = await Promise.all([getCatalog(), getOrgs(), listProjects()]);
        if (alive) setData({ catalog, orgs, projects });
      } catch (e) {
        if (alive) setLoadError(e instanceof ApiError ? e : new ApiError('server', 0, null, null));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (state.status !== 'authed') return null;

  if (loadError) {
    return (
      <Shell title="현장 만들기">
        <Stack gap="md" mt="md">
          <Title order={2}>현장 만들기</Title>
          {/* 연결 실패는 warn(다시 하면 된다), 그 밖은 danger. ErrorState.tsx 와 같은 기준. */}
          <Notice
            tone={loadError.kind === 'offline' || loadError.kind === 'unavailable' ? 'warn' : 'danger'}
          >
            {humanMessage(loadError)}
          </Notice>
          <Contact prefix="계속 안 되면 연락 주세요" />
        </Stack>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell title="현장 만들기">
        <Center h="40vh">
          <Stack align="center" gap="sm" className="cv-delayed">
            <Loader color="cv" />
            <Text size="sm">기능 목록을 읽는 중입니다</Text>
          </Stack>
        </Center>
      </Shell>
    );
  }

  const canCreate = data.orgs.filter((o) => o.can_create);
  if (canCreate.length === 0) {
    // ★ 「없습니다」로 단정하지 않는다. 이 사람은 조직 관리자가 아닐 뿐이고,
    //   권한을 받으면 바로 쓸 수 있다(Projects.tsx 의 빈 목록 문구와 같은 규율).
    return (
      <Shell title="현장 만들기">
        <Stack gap="md" mt="md">
          <Title order={2}>현장 만들기</Title>
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Text fw={700}>현장을 만들 수 있는 조직이 없습니다.</Text>
              <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
                현장 개설은 <b>조직 관리자</b>만 할 수 있습니다. 만들어야 할 현장이 있으면
                관리자에게 요청해 주세요.
              </Text>
              <Contact prefix="현장 개설 요청" />
            </Stack>
          </Card>
        </Stack>
      </Shell>
    );
  }

  return <NewProjectForm data={data} orgs={canCreate} />;
}

// ═══ 폼 ═══════════════════════════════════════════════════════════════════

function NewProjectForm({ data, orgs }: { data: Loaded; orgs: Org[] }) {
  const { catalog, projects } = data;

  const core = useMemo(() => catalog.modules.filter((m) => m.core), [catalog]);
  const opt = useMemo(() => catalog.modules.filter((m) => !m.core), [catalog]);
  /** 지금 조직에서 이미 쓰는 주소. ⚠️ **지워진 현장은 여기 없다**(slug.ts 참고). */
  const taken = useMemo(() => projects.map((p) => p.slug), [projects]);

  const [orgId, setOrgId] = useState(orgs[0]!.id);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [belt, setBelt] = useState(catalog.belts.includes('EPSG:5186') ? 'EPSG:5186' : catalog.belts[0]!);
  const [preset, setPreset] = useState<string | null>(DEFAULT_PRESET);
  const [on, setOn] = useState<Set<string>>(() => presetSet(opt.map((m) => m.id), DEFAULT_PRESET));
  const [openAdvanced, setOpenAdvanced] = useState(false);
  const [managers, setManagers] = useState<string[]>([]);
  const [mgrDraft, setMgrDraft] = useState('');

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [slow, setSlow] = useState(false);
  /** 주소 칸 **바로 밑에** 붙는 서버 오류. 전역 배너로 띄우면 어느 칸이 문제인지 모른다. */
  const [slugServerError, setSlugServerError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<CreateResult | null>(null);

  const slugRef = useRef<HTMLInputElement>(null);

  const check = checkSlug(slug, catalog.reserved, taken);
  const nameOk = name.trim().length > 0 && name.trim().length <= 120;
  const ready = nameOk && check.state === 'ok' && !busy;

  // ⚠️ `tree` 를 끄면 `phasing` 은 **켤 수 없다.** 장식이 아니라 데이터 무결성 방어다 —
  //    `create_project` 의 삽입 루프에 정렬이 없어서(007:227), phasing 이 tree 보다
  //    먼저 들어가면 트리거가 아직 없는 tree 행 대신 카탈로그 기본값(true)을 보고
  //    **통과시킨다.** 그러면 트리거가 막으려던 `phasing=true, tree=false` 가 만들어진다.
  //    반대 순서면 현장 생성 전체가 실패한다. 같은 입력이 실행마다 갈린다.
  const treeOff = !on.has('tree');
  useEffect(() => {
    if (treeOff && on.has('phasing')) {
      setOn((s) => {
        const n = new Set(s);
        n.delete('phasing');
        return n;
      });
    }
  }, [treeOff, on]);

  function applyPreset(p: string) {
    setPreset(p);
    setOn(presetSet(opt.map((m) => m.id), p));
  }

  function toggle(id: string) {
    setPreset(null); // 개별 토글을 만지면 프리셋 표시를 끈다. 안 그러면 거짓말이 된다
    setOn((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function addManager(raw: string) {
    // 붙여넣기가 기본 입력 방식이다 — 감리단 명단을 통째로 붙인다.
    const parts = raw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (parts.length === 0) return;
    setManagers((cur) => {
      const n = [...cur];
      for (const p of parts) if (p.includes('@') && !n.includes(p)) n.push(p);
      return n;
    });
    setMgrDraft('');
  }

  /** 주소 칸으로 되돌린다. **셋을 한 동작으로** — 스크롤 · 포커스 · 문장(aria-describedby). */
  function focusSlug(msg: string) {
    setSlugServerError(msg);
    setSubmitError(null);
    slugRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    slugRef.current?.focus();
  }

  // ── 제출 ──────────────────────────────────────────────────────────────
  async function submit() {
    setConfirming(false);
    setBusy(true);
    setSlow(false);
    setSubmitError(null);
    setSlugServerError(null);
    const slowTimer = window.setTimeout(() => setSlow(true), SLOW_HINT_MS);

    const modules: Record<string, boolean> = {};
    for (const m of opt) modules[m.id] = on.has(m.id);

    try {
      const res = await createProject({
        org_id: orgId,
        slug: slug.trim(),
        name: name.trim(),
        belt,
        modules,
        managers,
      });
      setDone(res);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;

      // ★★ 409 를 그냥 보여 주면 안 되는 이유가 여기 있다.
      //    느린 회선에서 두 번 누른 사람은 **자기가 3초 전에 만든 현장**을 두고
      //    "이미 쓰이고 있습니다 — 되돌려 받을 수 없습니다" 를 읽게 된다.
      //    → 먼저 목록을 다시 읽는다. 그 slug 가 있고 **내가 볼 수 있으면 성공이다.**
      if (code === 'slug_taken') {
        try {
          const again = await listProjects();
          const mine = again.find((p) => p.slug === slug.trim());
          if (mine) {
            setDone({
              project: {
                id: mine.id,
                slug: mine.slug,
                name: mine.name,
                belt: mine.belt,
                perms: mine.perms ?? [],
              },
              // 초대 결과는 첫 요청의 응답에 있었고 그건 못 받았다. 정직하게 비운다 —
              // 「담당자 추가하기」로 안내되므로 사용자가 확인할 수 있다.
              invites: [],
            });
            return;
          }
        } catch {
          /* 목록도 못 읽는 상황이면 아래 문구로 간다 */
        }
        focusSlug(
          `「${slug.trim()}」 은 이미 쓰이고 있습니다. 지운 현장이 쓰던 주소일 수도 있습니다 — 그 주소는 되돌려 받을 수 없습니다.`,
        );
        return;
      }

      if (code === 'reserved_slug') {
        focusSlug(`「${slug.trim()}」 은 시스템이 쓰는 주소라 쓸 수 없습니다.`);
        return;
      }
      if (code === 'bad_slug') {
        focusSlug('주소 형식이 맞지 않습니다. 영문 소문자로 시작하고, 하이픈은 가운데에만 올 수 있습니다.');
        return;
      }
      if (code === 'bad_module_combo' && e instanceof AdminError) {
        const mod = String(e.extra.module ?? '');
        const dep = String(e.extra.needs ?? '');
        setSubmitError(`「${labelOf(catalog, mod)}」 는 「${labelOf(catalog, dep)}」 없이 켤 수 없습니다.`);
        return;
      }
      if (code === 'bad_email' && e instanceof AdminError) {
        const bad = Array.isArray(e.extra.bad) ? (e.extra.bad as string[]).join(', ') : '';
        setSubmitError(`담당자 주소를 다시 봐 주세요: ${bad}`);
        return;
      }
      if (e instanceof ApiError && e.kind === 'offline') {
        // ★ **입력을 절대 날리지 않는다.** 마지막 문장이 핵심이다 —
        //   이게 없으면 사람이 새로고침해서 스스로 날린다.
        setSubmitError('연결이 끊겼습니다. 입력하신 내용은 그대로 있습니다. 다시 시도해 주세요.');
        return;
      }
      setSubmitError(humanMessage(e));
    } finally {
      window.clearTimeout(slowTimer);
      setBusy(false);
      setSlow(false);
    }
  }

  if (done) return <Created result={done} />;

  const onCount = on.size;
  const summary = (
    <Summary
      name={name}
      slug={slug}
      slugState={check.state}
      belt={belt}
      onCount={onCount}
      optTotal={opt.length}
      uploadOn={on.has('upload')}
      managers={managers}
      opt={opt.filter((m) => on.has(m.id))}
    />
  );

  return (
    <Shell title="현장 만들기">
      <style>{LAYOUT_CSS}</style>

      <Stack gap="lg">
        <Box>
          <Title order={2}>현장 만들기</Title>
          <Text size="sm" mt={4} style={{ color: 'var(--cv-ink-2)' }}>
            만들기를 누르기 전에 무엇이 만들어지는지 오른쪽에서 다 볼 수 있습니다.
          </Text>
        </Box>

        <div className="cvn-grid">
          {/* ── 왼쪽: 폼 ──────────────────────────────────────────────── */}
          <div className="cvn-form">
            <Stack gap="xl">
              {/* 1 · 어떤 현장인가 */}
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <Title order={3}>1 · 어떤 현장인가</Title>

                  {/* ⚠️ 조직이 둘 이상일 때만 그린다. v1 은 조직이 하나라 안 나온다.
                      그리고 조직 **이름**을 못 받는다(api.ts 의 Org.name 주석 참고) —
                      이름이 오기 전에는 id 앞 8자로 구분한다. 없는 이름을 지어내지 않는다. */}
                  {orgs.length > 1 ? (
                    <NativeSelect
                      label="조직"
                      description="이 현장이 속할 조직입니다."
                      value={orgId}
                      onChange={(e) => setOrgId(e.currentTarget.value)}
                      data={orgs.map((o) => ({ value: o.id, label: o.name ?? o.id.slice(0, 8) }))}
                    />
                  ) : null}

                  <TextInput
                    label="현장 이름"
                    description="화면에 뜨는 이름입니다. 한글·띄어쓰기 자유롭게."
                    placeholder="당진 고속도로"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    maxLength={120}
                    autoFocus
                  />

                  <Box>
                    <TextInput
                      ref={slugRef}
                      label="주소"
                      // ★ 폰에서 이 넷이 없으면 iOS 가 첫 글자를 대문자로 만들고,
                      //   사용자는 왜 ✕ 가 뜨는지 모른다. 자동 수정도 하이픈을 대시로 바꾼다.
                      inputMode="url"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="dangjin"
                      leftSection={
                        <Text size="sm" className="cv-mono" style={{ color: 'var(--cv-ink-2)' }}>
                          cv.kr/
                        </Text>
                      }
                      leftSectionWidth={62}
                      value={slug}
                      onChange={(e) => {
                        setSlug(e.currentTarget.value);
                        if (slugServerError) setSlugServerError(null);
                      }}
                      className="cv-mono"
                      maxLength={40}
                      error={!!slugServerError}
                      aria-invalid={slugServerError ? true : undefined}
                      aria-describedby="cvn-slug-msg"
                    />
                    {/* role="alert" — 확대해서 쓰는 사람은 화면 밖의 빨간 글씨를 못 본다 */}
                    <Text
                      id="cvn-slug-msg"
                      size="sm"
                      mt={6}
                      fw={check.state === 'ok' && !slugServerError ? 400 : 700}
                      role={slugServerError ? 'alert' : undefined}
                      // ⚠️ 여기는 `var(--mantine-color-red-8)`(#e03131) 이었다 —
                      //    **흰 위 4.35:1 · 어두운 면 위 3.65:1 로 둘 다 AA 미만**이다. [실측 2026-08-07]
                      //    slug 는 이 제품에서 **되돌릴 수 없는 유일한 결정**이라,
                      //    "이미 쓰는 주소입니다" 를 못 읽으면 그대로 확정된다. → 우리 위험색으로.
                      style={{
                        color: slugServerError
                          ? 'var(--cv-danger)'
                          : check.state === 'ok'
                            ? 'var(--cv-ok)'
                            : check.state === 'empty'
                              ? 'var(--cv-ink-2)'
                              : 'var(--cv-danger)',
                      }}
                    >
                      {slugServerError ?? check.msg}
                    </Text>
                    {/* ★ 「못 바꾼다」를 muted 12px 로 쓰지 마라. 야외에서 2.86:1 이라 안 읽힌다.
                        그리고 이건 이 제품에서 **되돌릴 수 없는 유일한 결정**이다. */}
                    <Text size="sm" mt={8} fw={700}>
                      한 번 정하면 바꿀 수 없습니다.
                    </Text>
                    <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
                      이 현장을 나중에 지워도 다른 현장이 이 주소를 다시 쓸 수 없습니다.
                      여러 공구로 나뉠 수 있으면 <span className="cv-mono">dangjin-1</span> 처럼
                      번호를 붙여 두세요.
                    </Text>
                  </Box>

                  <NativeSelect
                    label="좌표 원점대"
                    description="올라올 도면의 기준입니다."
                    value={belt}
                    onChange={(e) => setBelt(e.currentTarget.value)}
                    data={catalog.belts.map((b) => ({ value: b, label: beltLabel(b) }))}
                  />
                  {/* F6 — 모델이 생긴 뒤에는 DB 트리거가 막는다(008:129). 지금 말해 준다. */}
                  <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
                    모델을 한 건이라도 올린 뒤에는 <b>바꿀 수 없습니다</b> — 이미 선 모델의
                    위치가 통째로 어긋나기 때문입니다.
                  </Text>
                </Stack>
              </Card>

              {/* 2 · 기능 */}
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <Title order={3}>2 · 어떤 기능을 넣을 것인가</Title>

                  <Group gap="xs" wrap="wrap">
                    {PRESETS.map((p) => (
                      <Button
                        key={p.name}
                        size="sm"
                        variant={preset === p.name ? 'filled' : 'default'}
                        onClick={() => applyPreset(p.name)}
                        aria-pressed={preset === p.name}
                      >
                        {p.name}
                      </Button>
                    ))}
                  </Group>
                  <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
                    {preset
                      ? PRESETS.find((p) => p.name === preset)?.note
                      : '기능을 직접 고르셨습니다.'}{' '}
                    나중에 현장 설정에서 바꿀 수 있습니다.
                  </Text>

                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => setOpenAdvanced((v) => !v)}
                    aria-expanded={openAdvanced}
                  >
                    {openAdvanced ? '기능 접기' : `기능 하나씩 고르기 (${onCount}/${opt.length} 켜짐)`}
                  </Button>

                  {openAdvanced ? (
                    <Stack gap="lg">
                      <Box>
                        {/* ★ core 를 **숨기지 않는다.** 숨기면 나중에 "이력이 왜 켜져 있지?" 를
                            못 찾는다. 사유는 tooltip 이 아니라 **상시 문구**로 쓴다 —
                            터치 기기에는 hover 가 없어서 title 이 영원히 안 보인다. */}
                        <Text size="sm" fw={700}>
                          항상 켜져 있음
                        </Text>
                        <Text size="sm" mb={8} style={{ color: 'var(--cv-ink-2)' }}>
                          이 다섯은 끌 수 없습니다. 현장·구성원·기능 구성·이력·모델은 시스템이
                          돌아가는 데 필요합니다.
                        </Text>
                        <Group gap="xs" wrap="wrap">
                          {core.map((m) => (
                            <Chip key={m.id} label={m.name} id={m.id} on disabled />
                          ))}
                        </Group>
                      </Box>

                      <Box>
                        <Text size="sm" fw={700} mb={8}>
                          골라 넣는 기능 — {onCount}개 켜짐
                        </Text>
                        <Group gap="xs" wrap="wrap">
                          {opt.map((m) => {
                            const blocked = m.id === 'phasing' && treeOff;
                            return (
                              <Chip
                                key={m.id}
                                label={m.name}
                                id={m.id}
                                on={on.has(m.id)}
                                disabled={blocked}
                                onClick={() => toggle(m.id)}
                              />
                            );
                          })}
                        </Group>
                        {treeOff ? (
                          <Text size="sm" mt={8} fw={700} style={{ color: 'var(--cv-warn)' }}>
                            공정 4D 는 트리 없이 켤 수 없습니다 (일정을 트리에서 읽습니다).
                          </Text>
                        ) : null}
                      </Box>
                    </Stack>
                  ) : null}
                </Stack>
              </Card>

              {/* 3 · 담당자 */}
              <Card withBorder padding="lg">
                <Stack gap="md">
                  <Title order={3}>3 · 누가 이 현장을 맡나</Title>
                  <TextInput
                    label="프로젝트 매니저"
                    description="계정이 있으면 바로 담당자가 되고, 없으면 초대장이 갑니다. 여러 명은 쉼표나 줄바꿈으로 붙여 넣으셔도 됩니다."
                    placeholder="이메일을 넣고 Enter"
                    type="email"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    value={mgrDraft}
                    onChange={(e) => setMgrDraft(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      addManager(mgrDraft);
                    }}
                    onBlur={() => addManager(mgrDraft)}
                  />

                  {managers.length > 0 ? (
                    <Stack gap={6}>
                      {managers.map((m) => (
                        // ★ 주소를 **16px 고정폭 ink** 로 그린다. 목업의 12px 칩이 아니다 —
                        //   오타를 잡을 수 있는 유일한 사람이 지금 이 화면을 보는 사람인데,
                        //   그가 읽을 수 없는 크기로 그리면 오타는 영원히 안 잡힌다.
                        //   (오타가 나면 초대는 없는 사서함으로 가고, 화면에는 「대기 중」만 남는다.)
                        <Group key={m} gap="xs" wrap="nowrap" justify="space-between">
                          <Text size="md" className="cv-mono" style={{ wordBreak: 'break-all' }}>
                            {m}
                          </Text>
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => setManagers((c) => c.filter((x) => x !== m))}
                            aria-label={`${m} 빼기`}
                          >
                            빼기
                          </Button>
                        </Group>
                      ))}
                    </Stack>
                  ) : (
                    <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
                      비워 두고 만들어도 됩니다. 만든 뒤 구성원 화면에서 언제든 붙일 수 있습니다.
                    </Text>
                  )}
                </Stack>
              </Card>

              {/* 만들기 실패 — 사람이 지금 뭔가 해야 한다 → danger.
                  ⚠️ 화면을 갈아엎지 않는다(ErrorState.tsx 머리말). 장갑 끼고 채운 입력이 날아간다. */}
              {submitError ? <Notice tone="danger">{submitError}</Notice> : null}
            </Stack>
          </div>

          {/* ── 오른쪽: 요약 ──────────────────────────────────────────── */}
          <aside className="cvn-side">
            <Card withBorder padding="lg">
              <Stack gap="md">
                {summary}
                <div className="cvn-side-go">
                  <SubmitButton
                    ready={ready}
                    busy={busy}
                    slow={slow}
                    managers={managers.length}
                    onClick={() => setConfirming(true)}
                  />
                </div>
              </Stack>
            </Card>
          </aside>
        </div>
      </Stack>

      {/* ── 폰: 화면 아래 고정 바 ──────────────────────────────────────── */}
      <div className="cvn-bar">
        <Group justify="space-between" wrap="nowrap" gap="sm" px="md" py="sm">
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} truncate>
              {name.trim() || '이름 없음'}
            </Text>
            <Text size="sm" className="cv-mono" truncate style={{ color: 'var(--cv-ink-2)' }}>
              /{slug.trim() || '…'} · {onCount}개 켜짐
            </Text>
          </Box>
          <Button
            size="md"
            disabled={!ready}
            loading={busy}
            onClick={() => {
              // ⚠️ 폰에서는 주소 칸이 **화면 밖(위쪽)** 이다. 여기서 아무 일도 안 일어나면
              //    사용자는 버튼이 고장 났다고 생각한다 → 칸으로 데려간다.
              if (!ready && check.state !== 'ok') {
                focusSlug(check.msg);
                return;
              }
              setConfirming(true);
            }}
          >
            만들기
          </Button>
        </Group>
      </div>

      {/* ── 확인 ────────────────────────────────────────────────────────
          ★ **v1 에서 모달이 값을 하는 유일한 자리다.** 현장 개설은 드물고(§7),
            주소는 이 제품에서 되돌릴 수 없는 유일한 결정이다. 드물고 되돌릴 수 없는
            것에만 확인을 붙이면 확인창이 값을 한다. */}
      <Modal
        opened={confirming}
        onClose={() => setConfirming(false)}
        title="주소를 확인해 주세요"
        centered
      >
        <Stack gap="md">
          <Text className="cv-mono" fw={700} style={{ fontSize: 28, wordBreak: 'break-all' }}>
            cv.kr/{slug.trim()}
          </Text>
          <Text size="md" fw={700}>
            이 주소는 만든 뒤 바꿀 수 없습니다.
          </Text>
          <Text size="md">
            이 현장을 나중에 지워도 다른 현장이 이 주소를 다시 쓸 수 없습니다.
          </Text>
          <Group grow gap="sm" mt="sm">
            <Button variant="default" onClick={() => setConfirming(false)}>
              주소 고치기
            </Button>
            <Button onClick={() => void submit()} loading={busy}>
              이대로 만들기
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Shell>
  );
}

// ═══ 조각들 ═══════════════════════════════════════════════════════════════

function SubmitButton({
  ready,
  busy,
  slow,
  managers,
  onClick,
}: {
  ready: boolean;
  busy: boolean;
  slow: boolean;
  managers: number;
  onClick: () => void;
}) {
  return (
    <Stack gap="xs">
      <Button size="md" fullWidth disabled={!ready} loading={busy} onClick={onClick}>
        현장 만들기
      </Button>
      {slow ? (
        // 침묵이 재시도를 부르고, 재시도가 409 를 부르고, 409 가 최악의 오해를 부른다.
        <Text size="sm" fw={700} style={{ color: 'var(--cv-warn)' }} role="status">
          느립니다. 계속 기다리는 중입니다 — 다시 누르지 마세요.
        </Text>
      ) : managers > 0 ? (
        <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
          만들면 {managers}명에게 초대장이 갑니다.
        </Text>
      ) : null}
    </Stack>
  );
}

function Summary({
  name,
  slug,
  slugState,
  belt,
  onCount,
  optTotal,
  uploadOn,
  managers,
  opt,
}: {
  name: string;
  slug: string;
  slugState: string;
  belt: string;
  onCount: number;
  optTotal: number;
  uploadOn: boolean;
  managers: string[];
  opt: { id: string; name: string }[];
}) {
  return (
    <Stack gap="sm">
      <Text size="sm" fw={700} style={{ color: 'var(--cv-ink-2)' }}>
        만들면 이렇게 됩니다
      </Text>
      <Text fw={700} size="xl">
        {name.trim() || '이름 없음'}
      </Text>
      {/* slug 는 라벨이 아니라 **데이터**다 — muted 로 그리지 않는다 */}
      <Text className="cv-mono" size="md">
        cv.kr/{slug.trim() || '…'}
        {slugState === 'ok' ? null : (
          <Text component="span" size="sm" ml={6} style={{ color: 'var(--cv-ink-2)' }}>
            (아직 정해지지 않음)
          </Text>
        )}
      </Text>

      <Divider />

      <SumRow label="기능">
        {onCount}개 켜짐 <Text component="span" style={{ color: 'var(--cv-ink-2)' }}>/ {optTotal}</Text>
      </SumRow>
      <SumRow label="올릴 형식">
        {uploadOn ? (
          <span className="cv-mono">ifc · glb · b3dm · 폴더</span>
        ) : (
          '파일 올리기 꺼짐'
        )}
      </SumRow>
      <SumRow label="담당자">{managers.length > 0 ? `${managers.length}명` : '없음'}</SumRow>
      <SumRow label="좌표">
        <span className="cv-mono">{belt}</span>
      </SumRow>

      <Divider />

      <Box>
        <Text size="sm" fw={700} mb={6}>
          열람자가 보게 될 화면
        </Text>
        <Stack gap={2}>
          {opt.length === 0 ? (
            <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
              모델 보기만 됩니다.
            </Text>
          ) : (
            opt.slice(0, 8).map((m) => (
              <Text key={m.id} size="sm">
                — {m.name}
              </Text>
            ))
          )}
          {opt.length > 8 ? (
            <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
              … 외 {opt.length - 8}개
            </Text>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}

function SumRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm" align="baseline">
      {/* ⚠️ 표 머리글·라벨에 uppercase + letter-spacing 을 쓰지 마라. 한글에는 대소문자가
          없어서 uppercase 는 **죽은 선언**이고, 자간 확대는 음절 블록 리듬을 깨서
          오히려 읽기 어려워진다. 14px · ink-2 · 600 이면 「이름」이라는 신호는 충분하다. */}
      <Text size="sm" fw={600} style={{ color: 'var(--cv-ink-2)', whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      <Text size="sm" ta="right">
        {children}
      </Text>
    </Group>
  );
}

/** 기능 칩. 터치 대상 44px 을 **가로세로 둘 다** 지킨다(장갑 낀 엄지 접촉면이 34px 보다 넓다). */
function Chip({
  label,
  id,
  on,
  disabled,
  onClick,
}: {
  label: string;
  id: string;
  on: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Button
      size="sm"
      variant={on ? 'filled' : 'default'}
      disabled={disabled}
      onClick={onClick}
      aria-pressed={onClick ? on : undefined}
      /**
       * ⚠️ 여기 `opacity: disabled ? 0.55` 가 있었다. **빼야 한다.**
       *    투명도는 글자·바탕·테두리를 **한꺼번에** 흐리게 만들어서 대비를 통째로 깎는다.
       *    비활성 글자색(--cv-muted, 6.31)에 0.55 를 곱하면 다시 2.5:1 대가 된다 —
       *    global.css 가 방금 1.75:1 을 고쳐 놓은 것을 이 한 줄이 되돌린다.
       *    「못 누른다」는 신호는 global.css 의 `:disabled` 규칙 한 벌(바탕·글자·테두리)이 낸다.
       */
      styles={{ root: { minHeight: 44, minWidth: 44 } }}
    >
      <Group gap={6} wrap="nowrap">
        <span>{label}</span>
        {/* ⚠️ `opacity: 0.7` 도 뺐다. 12px 에 투명도까지 얹으면 theme.ts 가 **금지**한
            「12px + 흐린 글자」가 된다(야외 2.86:1). 이 값은 관리자가 문의할 때 그대로
            적어 보내는 **식별자**라 읽히지 않으면 쓸모가 없다. 12px 자체는 배지·식별자에
            허용된 크기다 — 문제는 크기가 아니라 조합이었다. */}
        <Text component="span" size="xs" className="cv-mono">
          {id}
        </Text>
      </Group>
    </Button>
  );
}

// ═══ 만들었습니다 ═════════════════════════════════════════════════════════

/**
 * ★ `/{slug}` 로 **바로 보내지 않는다.** 자동 이동하면 만들기 직후 LTE 에서 뷰어가
 *   타일을 빨기 시작하고, 소장은 그것을 "만들다가 멈췄다" 로 읽는다.
 *   그리고 무엇이 만들어졌는지(초대가 갔는지, 실패했는지)를 확인할 자리가 사라진다.
 */
function Created({ result }: { result: CreateResult }) {
  const { reload } = useSession();
  const { project, invites } = result;

  // ⚠️⚠️ **여기서 `reload()` 를 부르면 이 화면이 사라진다.** [실측 2026-08-06]
  //    `reload()` 가 세션 상태를 잠깐 `loading` 으로 만들고, App.tsx 의 관문이 그때
  //    <Booting/> 으로 화면을 통째로 갈아 끼운다 → 이 컴포넌트가 언마운트되면서
  //    `done` 상태가 날아가고, 돌아왔을 때는 **빈 만들기 폼**이다.
  //    사용자가 보는 것: 만들기를 눌렀는데 폼이 초기화됐다 = "안 만들어졌구나".
  //    → 목록 갱신은 **떠날 때** 한다. 아래 go().
  const [leaving, setLeaving] = useState(false);
  async function go(to: string) {
    setLeaving(true);
    // 목록에 새 현장이 있어야 App.tsx 의 ProjectRoute 가 404 를 안 준다.
    // 실패해도 그냥 간다 — 거기서 다시 확인한다.
    try {
      await reload();
    } catch {
      /* 무시 */
    }
    navigate(to);
  }

  const noManager = invites.length === 0;

  return (
    <Shell title={project.name}>
      <Stack gap="lg" mt="md">
        <Box>
          <Title order={2}>{project.name} 을(를) 만들었습니다.</Title>
          <Text className="cv-mono" size="md" mt={4}>
            cv.kr/{project.slug}
          </Text>
        </Box>

        <Card withBorder padding="lg">
          <Stack gap="sm">
            {noManager ? (
              <>
                <Text fw={700}>담당자가 아직 없습니다.</Text>
                <Text size="sm" style={{ color: 'var(--cv-ink-2)' }}>
                  담당자가 없으면 자료를 올릴 사람이 없습니다. 지금 붙이는 것이 가장 쉽습니다 —
                  이 화면을 닫으면 다시 알려 주지 않습니다.
                </Text>
              </>
            ) : (
              <Stack gap="xs">
                {invites.map((i) => (
                  <Group key={i.email} gap="sm" wrap="nowrap" align="baseline">
                    <Text size="md" className="cv-mono" style={{ wordBreak: 'break-all' }}>
                      {i.email}
                    </Text>
                    <Text
                      size="sm"
                      fw={i.kind === 'mail_failed' ? 700 : 400}
                      style={{
                        color:
                          i.kind === 'mail_failed' ? 'var(--cv-warn)' : 'var(--cv-ink-2)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {kindText(i)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>

        {/* warn 이다 — 「다시 보내기」로 풀린다. 초대장 자체는 이미 만들어져 있다. */}
        {invites.some((i) => i.kind === 'mail_failed') ? (
          <Notice tone="warn">
            메일 발송에 실패한 주소가 있습니다. <b>구성원 화면에서 다시 보내기</b>를 눌러 주세요.
            초대장 자체는 이미 만들어져 있습니다.
          </Notice>
        ) : null}

        <Group gap="sm" wrap="wrap">
          {/* 담당자가 0명이면 **주동작을 바꾼다.** 「현장 열기」를 크게 두면 그 사람은
              빈 뷰어를 보고 나가고, 담당자 없는 현장이 그대로 남는다. */}
          {noManager ? (
            <>
              <Button size="md" loading={leaving} onClick={() => void go(`/${project.slug}/members`)}>
                담당자 추가하기
              </Button>
              <Button variant="default" size="md" loading={leaving} onClick={() => void go(`/${project.slug}`)}>
                현장 열기
              </Button>
            </>
          ) : (
            <>
              <Button size="md" loading={leaving} onClick={() => void go(`/${project.slug}`)}>
                현장 열기
              </Button>
              <Button variant="default" size="md" loading={leaving} onClick={() => void go(`/${project.slug}/members`)}>
                구성원 관리
              </Button>
            </>
          )}
          <Button variant="subtle" size="md" loading={leaving} onClick={() => void go('/')}>
            현장 목록으로
          </Button>
        </Group>
      </Stack>
    </Shell>
  );
}

/** 역할 낱말을 사람 말로. ⚠️ `Members.tsx` 에 같은 것이 있다 — 한쪽만 고치지 마라. */
function roleText(id: string): string {
  return id === 'project_manager' ? '프로젝트 매니저' : id === 'viewer' ? '열람자' : id;
}

/**
 * 한 주소가 어떻게 됐는지 한 줄로.
 *
 * ⚠️ [고친 것 2026-08-11] 낱말(`k: string`)이 아니라 **줄 전체**를 받는다. 두 가지 때문이다.
 *    ㉠ 옛 코드의 `default: return k` 가 **모르는 낱말을 그대로 화면에 찍었다.** 서버가
 *       `role_differs`(017)를 새로 보내기 시작하자 사장님 화면에 영문 `role_differs` 가
 *       그대로 떴다. 이제 `InviteKind` 를 빠짐없이 다루므로, 서버가 낱말을 또 늘리면
 *       **화면에 새는 대신 `tsc` 가 먼저 막는다.**
 *    ㉡ `role_differs` 는 「지금 무슨 역할인지」를 같이 말해야 뜻이 있는데, 그 값이
 *       같은 줄의 `role_id` 에 실려 온다. 낱말만 받으면 쓸 재료가 없다.
 * ⚠️ `Members.tsx` 에 같은 이름의 쌍둥이가 있다(문구가 조금 다르다 — 여기는 「만들었습니다」
 *    직후 화면이라 「담당자」로 말한다). 문구를 손보면 양쪽을 같이 봐라.
 */
function kindText(x: InviteResultRow): string {
  switch (x.kind) {
    case 'member':
      return '바로 담당자가 되었습니다';
    case 'already':
      return '이미 구성원이었습니다';
    // ★ [017] **아무 일도 안 일어났다**를 정직하게 말한다. 서버는 `on conflict … do nothing`
    //   이라 역할을 안 바꾼다 — 그 사실을 안 말하면 만든 사람은 바뀐 줄 알고 넘어간다.
    case 'role_differs':
      // ⚠️ `role_id` 는 이 낱말과 한 벌로 오지만, 없을 때 빈칸을 그대로 끼우면
      //    「지금 입니다」가 나온다. 모르면 모른다고 쓴다.
      return x.role_id
        ? `이미 구성원입니다 — 지금 ${roleText(x.role_id)}입니다. 역할은 바뀌지 않았습니다`
        : '이미 구성원입니다 — 역할은 바뀌지 않았습니다';
    case 'invited':
      return '초대장을 보냈습니다 (14일)';
    case 'reinvited':
      return '새 초대장을 보냈습니다 (14일)';
    case 'mail_failed':
      return '메일 발송에 실패했습니다';
  }
}

// ═══ 도우미 ═══════════════════════════════════════════════════════════════

function presetSet(allOptional: string[], presetName: string): Set<string> {
  const p = PRESETS.find((x) => x.name === presetName);
  const off = new Set(p?.off ?? []);
  return new Set(allOptional.filter((id) => !off.has(id)));
}

function labelOf(catalog: Catalog, id: string): string {
  return catalog.modules.find((m) => m.id === id)?.name ?? id;
}

function beltLabel(b: string): string {
  const names: Record<string, string> = {
    'EPSG:5185': '서부원점 · EPSG:5185',
    'EPSG:5186': '중부원점 · EPSG:5186 (당진 포함)',
    'EPSG:5187': '동부원점 · EPSG:5187',
    'EPSG:5188': '동해원점 · EPSG:5188',
  };
  return names[b] ?? b;
}

// ═══ 배치 ═════════════════════════════════════════════════════════════════
//
//  ★ 격자 접힘과 하단 고정 바를 **같은 미디어쿼리 하나**로 켠다.
//    폭 판정을 JS(useIsPhone, 720px)로 또 하면 값이 갈린다 — 지금 저장소에 분기점이
//    576 · 720 · 768 · 820 으로 넷이고, 750px(세로 아이패드·폴드)에서 격자는 접히는데
//    sticky 는 안 붙어서 **만들기 버튼이 폼 맨 아래에 파묻힌다.** 여기서는 그 자리가 없다.
//  ★ 320px 고정 폭을 쓰지 않는다. 브라우저 확대 150%(853px)에서 두 칸이 유지되는데
//    320px 패널이 533px 안에 들어가야 해서 요약이 뭉개진다. `minmax(min(320px,40%),…)` 다.
//  ★ 폼 폭은 px 이 아니라 `ch` 다. px 로 잡으면 확대할 때마다 다시 깨진다.

const LAYOUT_CSS = `
.cvn-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(min(320px, 40%), 340px);
  gap: 24px;
  align-items: start;
}
.cvn-form { max-width: 68ch; }
.cvn-side { position: sticky; top: 12px; }
.cvn-bar { display: none; }

@media (max-width: 820px) {
  .cvn-grid { grid-template-columns: 1fr; }
  .cvn-form { max-width: none; }
  .cvn-side { position: static; }
  .cvn-side-go { display: none; }
  .cvn-bar {
    display: block;
    position: sticky;
    bottom: 0;
    margin: 12px -12px 0;
    background: var(--mantine-color-body);
    border-top: 1px solid var(--mantine-color-default-border);
    /* 폰 가로화면 노치·홈바 */
    padding-bottom: env(safe-area-inset-bottom);
    z-index: 5;
  }
}
`;
